// Correo saliente (SMTP) del módulo Nómina — para el envío de roles de pago.
// Nómina → Configuración → Correo (SMTP). La clave se cifra en reposo y es
// write-only (en blanco = conservar la actual).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls, nomRpc, type SmtpConfigData } from './shared';

export function SmtpNomina() {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<SmtpConfigData>({
    queryKey: ['nom-smtp'],
    queryFn: () => nomRpc<SmtpConfigData>('nom_smtp_get', {}),
  });

  const [form, setForm] = useState({ host: '', puerto: '587', seguro: false, usuario: '', password: '', from_email: '', from_nombre: '', activo: true });

  useEffect(() => {
    if (data) {
      setForm({
        host: data.host ?? '', puerto: String(data.puerto ?? 587), seguro: !!data.seguro,
        usuario: data.usuario ?? '', password: '',
        from_email: data.from_email ?? '', from_nombre: data.from_nombre ?? '', activo: data.activo,
      });
    }
  }, [data]);

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_smtp_set', { _data: { ...form, puerto: Number(form.puerto) || 587 } }),
    onSuccess: () => { toast.success('Configuración SMTP guardada'); void qc.invalidateQueries({ queryKey: ['nom-smtp'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold">Correo saliente de Nómina (SMTP)</h1>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>
      <p className="text-sm text-muted-foreground">
        Estas credenciales se usan para enviar los roles de pago a los empleados (PDF protegido con su cédula).
        Son independientes de los canales del módulo médico.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Servidor SMTP</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Campo label="Servidor (host) *" className="col-span-2"><input className={fieldCls} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="smtp.gmail.com" /></Campo>
            <Campo label="Puerto *"><input type="number" className={fieldCls} value={form.puerto} onChange={(e) => setForm({ ...form, puerto: e.target.value })} /></Campo>
          </div>
          <Switch checked={form.seguro} onChange={(v) => setForm({ ...form, seguro: v })} label="TLS directo (puerto 465) — apagado = STARTTLS (587)" />
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Usuario"><input className={fieldCls} value={form.usuario} onChange={(e) => setForm({ ...form, usuario: e.target.value })} placeholder="nomina@empresa.com" /></Campo>
            <Campo label={`Clave ${data?.password_configurada ? '(configurada — en blanco = conservar)' : '*'}`}>
              <input type="password" className={fieldCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            </Campo>
            <Campo label="Correo remitente (from) *"><input className={fieldCls} value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="nomina@empresa.com" /></Campo>
            <Campo label="Nombre del remitente"><input className={fieldCls} value={form.from_nombre} onChange={(e) => setForm({ ...form, from_nombre: e.target.value })} placeholder="Nómina Mi Empresa" /></Campo>
          </div>
          <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Canal activo" />
          <div className="flex justify-end pt-2">
            <Button isLoading={guardar.isPending} disabled={!form.host.trim() || !form.from_email.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        La clave se guarda cifrada en la base de datos (pgcrypto) y solo el worker de envío puede descifrarla.
        Recuerda tener el servicio <span className="font-mono">notificador</span> en ejecución para que los correos salgan.
      </p>
    </div>
  );
}
